import { useState } from 'react'

import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import {
  ClientsTableDocument,
  ContactsImportValidationDocument,
  ContactsImportValidationMutation,
  ContactsImporterDocument,
  ContactsImporterMutation,
  VendorsTableDocument,
} from 'generated/resources/graphql'

type ImportFinanceContactsImportResultPayload = NonNullable<
  ContactsImporterMutation['financeMutations']['importFinanceContactsImportResult']
>
type ValidateFinanceContactsImportResultPayload = NonNullable<
  ContactsImportValidationMutation['financeMutations']['validateFinanceContactsImportResult']
>

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'

export const useUploadContactsImportFile = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const [uploadContactsImportFile, { loading, data }] = useMutation(ContactsImporterDocument, {
    extractorFn: (res) => res.financeMutations.importFinanceContactsImportResult,
    refetchQueries: [ClientsTableDocument, VendorsTableDocument],
  })
  const [uploadContactsImportFileCheck, { loading: loadingCheck, data: check }] = useMutation(
    ContactsImportValidationDocument,
    {
      extractorFn: (res) => res.financeMutations.validateFinanceContactsImportResult,
    }
  )

  const [errorChecks, setErrorChecks] = useState<string[] | null>(null)
  const [errors, setErrors] = useState<string[] | null>(null)
  const [rowsToBeUpdated, setRowsToBeUpdated] = useState<number | null>(null)
  const [rowsToBeAdded, setRowsToBeAdded] = useState<number | null>(null)
  const [rowsUpdated, setRowsUpdated] = useState<number | null>(null)
  const [rowsAdded, setRowsAdded] = useState<number | null>(null)

  const getErrorsInStringArray = (
    errorsParam:
      | ValidateFinanceContactsImportResultPayload['errors']
      | ImportFinanceContactsImportResultPayload['errors']
  ) => {
    return errorsParam
      .map((el) => {
        if (el.__typename === 'SimpleError') {
          return el.__typename
        } else if (el.__typename === 'StructuredError') {
          return el.field === 'validation' ? el.messages : []
        } else {
          return JSON.stringify(el)
        }
      })
      .flat()
  }

  useUnsafeEffect(() => {
    if (check) {
      const checkErrors = check.financeMutations.validateFinanceContactsImportResult.errors
      if (checkErrors.length > 0) {
        setErrorChecks(getErrorsInStringArray(checkErrors))
      } else {
        const result =
          check.financeMutations.validateFinanceContactsImportResult.contactsImportResult
        setRowsToBeAdded(result ? result.added : 0)
        setRowsToBeUpdated(result ? result.updated : 0)
      }
    }
  }, [check])

  useUnsafeEffect(() => {
    if (data) {
      const dataErrors = data.financeMutations.importFinanceContactsImportResult.errors
      if (dataErrors.length > 0) {
        setErrors(getErrorsInStringArray(dataErrors))
      } else {
        const result = data.financeMutations.importFinanceContactsImportResult.contactsImportResult
        setRowsAdded(result ? result.added : 0)
        setRowsUpdated(result ? result.updated : 0)
      }
    }
  }, [data])

  return {
    loading,
    loadingCheck,
    rowsToBeAdded,
    rowsToBeUpdated,
    rowsAdded,
    rowsUpdated,
    errorChecks,
    errors,
    uploadImportFileToCheck(file: File, contactType: FinanceValueObjectsContactTypeEnum) {
      setErrorChecks(null)
      setRowsToBeAdded(null)
      setRowsToBeUpdated(null)
      return uploadContactsImportFileCheck({
        variables: {
          companyId,
          file,
          contactType,
        },
      })
    },
    uploadImportFile(file: File, contactType: FinanceValueObjectsContactTypeEnum) {
      setRowsAdded(null)
      setRowsUpdated(null)
      setErrors(null)
      return uploadContactsImportFile({
        variables: {
          companyId,
          file,
          contactType,
        },
      })
    },
  }
}
